import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { haptic } from '../../hooks/useTelegram';
import { useWallets } from '../../store/wallets';
import { copyText } from '../../lib/clipboard';
import { FigIcon } from '../FigIcon';
import { CoinIcon } from '../CoinIcon';
import { WalletSwitcher } from './WalletSwitcher';

/** Receive / QR screen (Figma node 2:3532). A wallet switcher sits at the top so
 *  the user can flip wallets in place; the address/QR follow the selection. On
 *  TRON, TRX and every TRC-20 token (incl. USDT) share one address, so there is
 *  a single QR card — no network switcher. */
export function ReceiveScreen() {
  const { t } = useTranslation();
  const wallets = useWallets((s) => s.wallets);
  // Same selection the rest of the app uses — switching here also decides where
  // the next send or purchase comes from.
  const activeId = useWallets((s) => s.activeId) ?? wallets[0]?.id ?? '';
  const setActiveId = useWallets((s) => s.setActive);

  const wallet = wallets.find((w) => w.id === activeId) ?? wallets[0]!;
  const address = wallet.address;

  const copy = () => void copyText(address);

  const share = () => {
    haptic('medium');
    void navigator.share?.({ text: address }).catch(() => {});
  };

  return (
    <div className="page-enter flex min-h-full flex-col bg-surface px-4 pb-6 pt-3">
      {/* header: wallet switcher (centered); back is the native Telegram button */}
      <div className="flex items-center justify-center">
        <WalletSwitcher wallets={wallets} activeId={activeId} onSelect={setActiveId} />
      </div>

      {/* Real QR encoding THIS wallet's TRON address (level H so the centered
          TRX/USDT badge doesn't break scanning). */}
      {/* White in both themes — see TelegramGate: the quiet zone has to stay light. */}
      <div className="relative mt-4 flex justify-center rounded-[32px] bg-white p-6 shadow-card">
        <QRCodeSVG
          value={address}
          size={264}
          level="H"
          marginSize={0}
          bgColor="#ffffff"
          fgColor="#10131a"
          className="h-auto w-full max-w-[264px]"
        />
        <span
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-card shadow-[0_2px_8px_rgba(0,0,0,0.18)]"
          style={{ width: 54, height: 54 }}
        >
          <span className="flex -space-x-[12px]">
            <CoinIcon symbol="TRX" size={30} />
            <CoinIcon symbol="USDT" size={30} />
          </span>
        </span>
      </div>

      {/* address */}
      <div className="mt-6 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <p className="text-[15px] font-medium text-muted">{t('receive.yourAddress')}</p>
        </div>
        <button
          onClick={copy}
          className="flex items-start gap-2 rounded-[26px] bg-card p-4 text-left active:opacity-80"
        >
          <span className="flex-1 break-all text-[16px] leading-[1.4] text-ink">
            {address}
            <FigIcon
              name="copy"
              size={18}
              className="ml-[6px] inline-block translate-y-[3px] text-muted"
            />
          </span>
        </button>
      </div>

      {/* one-address notice — TRX and USDT share the same TRON address */}
      <div className="mt-4 flex items-center gap-3 rounded-[20px] bg-card p-4">
        <span className="flex shrink-0 -space-x-[10px]">
          <span className="grid place-items-center rounded-full ring-2 ring-white">
            <CoinIcon symbol="TRX" size={30} />
          </span>
          <span className="grid place-items-center rounded-full ring-2 ring-white">
            <CoinIcon symbol="USDT" size={30} />
          </span>
        </span>
        <p className="flex-1 text-[13px] leading-[1.35] text-muted">
          {t('receive.sameAddress')}
        </p>
      </div>

      <div className="flex-1" />

      {/* share */}
      <button
        onClick={share}
        className="mt-8 h-[56px] w-full rounded-full bg-accent text-[16px] font-semibold text-white active:bg-accent-ink"
      >
        {t('receive.share')}
      </button>
    </div>
  );
}
